/**
 * PATH: packages/ui/src/components/settings/sender-profiles/senderProfilesApi.ts
 * FILE_ID: UI-SETTINGS-SENDERS-API-027
 * PURPOSE: Thin I→O wrappers over the counterparty stewardship endpoints.
 * WHY: Keep the section component free of URL/unwrap plumbing (same
 *      pattern as labelPreferencesApi).
 *
 * Endpoints (apiClient baseURL already includes `/api`):
 *   GET  /kanban/senders/                 -> {profiles, total, roles}
 *   POST /kanban/senders/feedback/        -> {event_id, kind, domain}
 *   POST /kanban/senders/company/         -> {client_id, client_name,
 *                                             created, anchored}   (029)
 *   GET  /kanban/senders/merges/          -> {candidates, total}   (P4)
 *   POST /kanban/senders/merges/verdict/  -> {candidate_id, verdict, status}
 *   GET  /kanban/persons/                 -> {persons, total}      (V8)
 * BACKEND: quotech-backend email_kanban/views/senders.py (cross-repo,
 *   so not a RELATED edge the FE registry can resolve).
 */

import type { SettingsApiClient } from '../types'
import type {
  PersonMergeQueueResponse,
  PersonOverviewsResponse,
  SenderProfilesResponse,
  SenderVerdictInput,
} from './types'

// Some clients return the axios response ({data}), others the body directly.
const unwrap = (res: unknown) =>
  res && typeof res === 'object' && 'data' in res
    ? (res as { data: unknown }).data
    : res

export async function fetchSenderProfiles(
  api: SettingsApiClient,
  params: { search?: string; role?: string; limit?: number; offset?: number } = {},
): Promise<SenderProfilesResponse> {
  const qs = new URLSearchParams()
  if (params.search) qs.set('search', params.search)
  if (params.role) qs.set('role', params.role)
  if (params.limit != null) qs.set('limit', String(params.limit))
  if (params.offset != null) qs.set('offset', String(params.offset))
  const suffix = qs.toString() ? `?${qs.toString()}` : ''
  const body = unwrap(
    await api.get(`/kanban/senders/${suffix}`),
  ) as Partial<SenderProfilesResponse> | null | undefined
  return {
    profiles: Array.isArray(body?.profiles) ? body.profiles : [],
    total: typeof body?.total === 'number' ? body.total : 0,
    roles: Array.isArray(body?.roles) ? body.roles : [],
  }
}

export async function sendSenderVerdict(
  api: SettingsApiClient,
  verdict: SenderVerdictInput,
): Promise<{ event_id: string; kind: string; domain: string }> {
  return unwrap(await api.post('/kanban/senders/feedback/', verdict)) as {
    event_id: string
    kind: string
    domain: string
  }
}

/** 029 — correct which CRM company a sender domain maps to. `anchored`
 *  false means the client's email columns were both occupied by other
 *  domains, so the link won't survive re-resolution (FE must say so). */
export async function setSenderCompany(
  api: SettingsApiClient,
  input: { domain: string; client_name?: string; client_id?: number },
): Promise<{
  client_id: number
  client_name: string
  created: boolean
  anchored: boolean
}> {
  return unwrap(await api.post('/kanban/senders/company/', input)) as {
    client_id: number
    client_name: string
    created: boolean
    anchored: boolean
  }
}

/** P4 review queue: pending person merge candidates, oldest first. */
export async function fetchMergeCandidates(
  api: SettingsApiClient,
  params: { limit?: number; offset?: number } = {},
): Promise<PersonMergeQueueResponse> {
  const qs = new URLSearchParams()
  if (params.limit != null) qs.set('limit', String(params.limit))
  if (params.offset != null) qs.set('offset', String(params.offset))
  const suffix = qs.toString() ? `?${qs.toString()}` : ''
  const body = unwrap(
    await api.get(`/kanban/senders/merges/${suffix}`),
  ) as Partial<PersonMergeQueueResponse> | null | undefined
  return {
    candidates: Array.isArray(body?.candidates) ? body.candidates : [],
    total: typeof body?.total === 'number' ? body.total : 0,
  }
}

/** W3-V8: cross-nested people at a domain (addresses/roles/emails/docs/jobs). */
export async function fetchPersonOverviews(
  api: SettingsApiClient,
  params: { domain?: string; sender?: string; search?: string } = {},
): Promise<PersonOverviewsResponse> {
  const qs = new URLSearchParams()
  if (params.domain) qs.set('domain', params.domain)
  if (params.sender) qs.set('sender', params.sender)
  if (params.search) qs.set('search', params.search)
  const body = unwrap(
    await api.get(`/kanban/persons/?${qs.toString()}`),
  ) as Partial<PersonOverviewsResponse> | null | undefined
  return {
    persons: Array.isArray(body?.persons) ? body.persons : [],
    total: typeof body?.total === 'number' ? body.total : 0,
  }
}

/** Accept merges the pair (IDs survive); reject vetoes it forever. */
export async function sendMergeVerdict(
  api: SettingsApiClient,
  input: { candidate_id: string; verdict: 'accept' | 'reject' },
): Promise<{ candidate_id: string; verdict: string; status: string }> {
  return unwrap(await api.post('/kanban/senders/merges/verdict/', input)) as {
    candidate_id: string
    verdict: string
    status: string
  }
}
